import type { GameSnapshot } from '../shared/types.js';

const STYLE_ID = 'drone-warning-style';
const LOST_NOTICE_MS = 7000;

const ensureStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .drone-warning-banner { position: fixed; top: 0.6rem; left: 50%; transform: translateX(-50%); z-index: 2147483000;
      max-width: min(94vw, 34rem); padding: 0.55rem 0.9rem; border-radius: 4px; text-align: center;
      font: 700 0.85rem/1.3 "Chakra Petch", system-ui, sans-serif; letter-spacing: 0.06em; text-transform: uppercase;
      color: #fff; background: rgba(190, 30, 25, 0.92); border: 1px solid rgba(255, 170, 160, 0.85);
      box-shadow: 0 0 18px rgba(255, 60, 40, 0.5); pointer-events: none; display: none; }
    .drone-warning-banner.visible { display: block; animation: drone-warning-blink 0.9s ease-in-out infinite; }
    .drone-warning-banner.notice { background: rgba(60, 60, 70, 0.94); border-color: rgba(200, 210, 220, 0.7); box-shadow: none; animation: none; }
    @keyframes drone-warning-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
  `;
  document.head.appendChild(style);
};

const knownDrones = new Map<string, { name: string; mine: boolean; mode: string }>();
let lostNotice: { text: string; until: number } | null = null;

let bannerEl: HTMLDivElement | null = null;

const getBanner = (): HTMLDivElement => {
  if (bannerEl?.isConnected) return bannerEl;
  ensureStyle();
  const banner = document.createElement('div');
  banner.className = 'drone-warning-banner';
  banner.setAttribute('role', 'alert');
  document.body.append(banner);
  bannerEl = banner;
  return banner;
};

export const updateDroneWarnings = (snapshot: GameSnapshot, playerId: string | null | undefined): void => {
  const drones = snapshot.drones ?? [];
  const mine = playerId ?? '';
  const now = Date.now();

  const presentIds = new Set(drones.map((drone) => drone.id));
  for (const [id, info] of knownDrones) {
    if (!presentIds.has(id)) {
      if (info.mine) lostNotice = { text: `Drone ${info.name} was lost forever.`, until: now + LOST_NOTICE_MS };
      knownDrones.delete(id);
    }
  }
  for (const drone of drones) {
    const involvesMe = drone.controllerId === mine || (drone.mode === 'disconnected' && drone.lastControllerId === mine);
    knownDrones.set(drone.id, { name: drone.name, mine: involvesMe || (knownDrones.get(drone.id)?.mine ?? false) && drone.mode !== 'docked', mode: drone.mode });
  }

  const waiting = drones.filter((drone) => drone.mode === 'disconnected' && drone.lastControllerId === mine && mine !== '');
  const element = getBanner();
  if (waiting.length > 0) {
    const worst = waiting.reduce((a, b) => ((a.lostInMs ?? 0) <= (b.lostInMs ?? 0) ? a : b));
    const seconds = Math.max(0, Math.ceil((worst.lostInMs ?? 0) / 1000));
    element.textContent = `Drone ${worst.name} connection lost! Reconnect within ${seconds} seconds or lose it forever!`;
    element.className = 'drone-warning-banner visible';
  } else if (lostNotice && lostNotice.until > now) {
    element.textContent = lostNotice.text;
    element.className = 'drone-warning-banner visible notice';
  } else {
    element.className = 'drone-warning-banner';
  }
};
